import schoolSuggestion from '../handlebars/templates/schoolSuggestion.tmpl';
import teacherSuggestion from '../handlebars/templates/teacherSuggestion.tmpl';
import { type ChangeEvent, PureComponent, createRef } from 'react';
import { load } from './keywordBloodhounds.js';
import { make } from './districtBloodhound.js';
import { convertKeywordsToFilter } from './convertKeywordsToFilter';
import { getURLParam } from '../adapters/dupes/queryParams.js';
import './external-adapters/typeaheadJqueryAdapter.js';
import { buildHackySchoolPageUrl } from './buildHackySchoolPageUrl.js';
import type { FilterCategory } from '../search/types/searchFilter';
import type { SearchCallbacks } from '../search/types/searchPage';
import type {
    DistrictSuggestion,
    KeywordSuggestion,
    SchoolSuggestion,
    TeacherSuggestion,
    TypeaheadSuggestion,
} from '../search/types/typeaheadSuggestions';

interface KeywordSearchBoxProps {
    searchURL: string;
    baseSearchURL: string;
    callbacks: SearchCallbacks;
    searchAll?: () => void;
    filterCategories?: FilterCategory[];
    extraFilters?: FilterCategory[];
    placeholder?: string;
    searchOnBlur?: boolean;
    // For parent components that need to be aware of the component's keyword state
    setPendingKeywords?: (keywords: string) => void;
}

interface KeywordSearchBoxState {
    keywords: string;
}

export class KeywordSearchBox extends PureComponent<KeywordSearchBoxProps, KeywordSearchBoxState> {
    static defaultProps = {
        placeholder: 'Search topics, teachers & schools',
    };

    private readonly inputRef = createRef<HTMLInputElement>();

    searchName = 'keyword';

    state: KeywordSearchBoxState = {
        keywords: this.extractKeywords(this.props),
    };

    extractKeywords(props: KeywordSearchBoxProps) {
        return getURLParam(props.searchURL, 'keywords') || '';
    }

    componentDidMount() {
        const bloodhounds = load();
        const $input = $(this.inputRef.current!);

        // It isn't worth the time to create types to satisfy the generics
        // of a library that has been deprecated for years.
        // We'll need to replace this code eventually.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        $input.typeahead<any>({
            highlight: true,
            hint: false,
        }, {
            name: 'keywords',
            display: 'value',
            source: bloodhounds.keywords.ttAdapter(),
            templates: {
                suggestion: function (word: KeywordSuggestion) {
                    return "<div class='suggestion suggestion-primary keyword-suggestion'>" + word.value + '</div>';
                },
            },
        }, {
            name: 'teachers',
            display: function (item: TeacherSuggestion) { return item.teacherName ? (item.teacherName + ' - ' + item.schoolName + ', ' + item.cityName + ', ' + item.stateCode) : ''; },
            source: bloodhounds.teachers.ttAdapter(),
            templates: {
                suggestion: teacherSuggestion,
                header: "<div class='separator'>Teachers</div>",
            },
        }, {
            name: 'schools',
            display: function (item: SchoolSuggestion) { return item.schoolName ? (item.schoolName + ' - ' + item.address1 + ', ' + item.cityName + ', ' + item.stateCode) : ''; },
            source: bloodhounds.schools.ttAdapter(),
            templates: {
                suggestion: schoolSuggestion,
                header: "<div class='separator'>Schools</div>",
            },
        }, {
            name: 'districts',
            display: function (item: DistrictSuggestion) { return item.name + ', ' + item.stateCode; },
            source: make().ttAdapter(),
            templates: {
                header: "<div class='separator'>School districts</div>",
                suggestion: function (district: DistrictSuggestion) {
                    return "<div class='suggestion location-suggestion'>" +
                            district.name + ', ' + district.stateCode +
                            '</div>';
                },
            },
        }).on('typeahead:selected', (_event: unknown, suggestion: TypeaheadSuggestion, dataName: string) => {
            if (dataName === 'schools') {
                const schoolSuggestion = suggestion as SchoolSuggestion;
                if (schoolSuggestion.moreResults) {
                    this.validateKeywordsForm(true, null);
                } else {
                    window.location.href = buildHackySchoolPageUrl(schoolSuggestion.schoolId);
                }
            } else if (dataName === 'teachers') {
                const teacherSuggestion = suggestion as TeacherSuggestion;
                if (teacherSuggestion.moreResults) {
                    this.validateKeywordsForm(null, true);
                } else {
                    window.createCookie('searchedForTeacher', teacherSuggestion.teacherId, 1);
                    window.location.href = `${baseURL}classroom/${teacherSuggestion.teacherId}`;
                }
            } else if (dataName === 'districts') {
                const districtSuggestion = suggestion as DistrictSuggestion;
                this.setState({ keywords: '' });
                this.props.callbacks.applyFilters({ community: `${districtSuggestion.districtId}:2` });
            } else {
                if (dataName === 'keywords') {
                    this.setKeywords((suggestion as KeywordSuggestion).value, this.submit);
                } else {
                    this.submit();
                }
            }
        }).on('typeahead:autocompleted', (_event: unknown, suggestion: KeywordSuggestion, dataName: string) => {
            if (dataName === 'keywords') {
                this.setKeywords(suggestion.value);
            }
        }).keypress((e) => {
            if (e.keyCode === 13) {
                this.submit();
            }
        });
    }

    componentWillUnmount() {
        if (this.inputRef.current) {
            $(this.inputRef.current).typeahead('destroy');
        }
    }

    // TODO: Should this change to componentDidUpdate?
    UNSAFE_componentWillReceiveProps(nextProps: KeywordSearchBoxProps) {
        const $input = $(this.inputRef.current!);
        const keywords = this.extractKeywords(nextProps);

        if (keywords !== this.extractKeywords(this.props)) {
            this.setState({ keywords });
            $input.typeahead('val', keywords);
            $input.typeahead('close');
        }
    }

    onBlur = () => {
        if (this.props.searchOnBlur) {
            this.submit();
        }
    };

    submit = () => {
        (this.props.searchAll || this.validateKeywordsForm)();
        this.props.setPendingKeywords?.('');
        // Without this, keyword suggestions stayed open if you used enter to submit
        // a keyword that was converted into a discrete filter after submission
        if (this.inputRef.current) {
            $(this.inputRef.current).typeahead('close');
        }
    };

    /**
     * @param schoolsOnly whether to only search for schools
     * @param teachersOnly whether to only search for teachers
     */
    makeURL = (schoolsOnly?: boolean | null, teachersOnly?: boolean | null) => {
        const { searchURL, filterCategories = [], extraFilters = [], callbacks } = this.props;
        const { makeSearchURL } = callbacks;
        const keywords = this.state.keywords.replace(/<script>/i, '');

        if (keywords === this.extractKeywords(this.props)) {
            return searchURL;
        }

        window.dcAnalytics.track('Search', { Keyword: keywords });

        if (!schoolsOnly && !teachersOnly) {
            const allFilterCategories = [...filterCategories, ...extraFilters];
            const filter = convertKeywordsToFilter(keywords, allFilterCategories);

            if (filter) {
                const param: { [key: string]: string } = {};
                param[filter.paramName] = filter.value;
                return makeSearchURL(param);
            }
        }

        const params = {
            keywords,
            widenRetry: null,
            schoolsOnly,
            teachersOnly,
        };
        return makeSearchURL(params);
    };

    /**
     * @param schoolsOnly whether to only search for schools
     * @param teachersOnly whether to only search for teachers
     */
    validateKeywordsForm = (schoolsOnly?: boolean | null, teachersOnly?: boolean | null) => {
        this.props.callbacks.changeURL(this.makeURL(schoolsOnly, teachersOnly));
    };

    handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        this.setKeywords(event.target.value.replace(/^\s+/, ''));
    };

    setKeywords = (keywords: string, callback?: () => void) => {
        this.setState({
            keywords,
        }, callback);
        this.props.setPendingKeywords?.(keywords);
    };

    render() {
        return (
            <input
                ref={this.inputRef}
                className="search-box"
                type="text" name="keywords"
                placeholder={this.props.placeholder} onBlur={this.onBlur}
                value={this.state.keywords} onChange={this.handleChange}
                aria-label="Keyword search: Suggestions appear below" />
        );
    }
}
